import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SetupConfig } from '@/services/auth.service';
import type { OverrideChoice } from '@/services/uploads.service';
import {
  DEFAULT_SELECTED_FIELDS,
  type PrimaryIdentifier,
} from '@/lib/klaviyoPayload';

// Selectable enrichment features (coming-soon boxes are display-only and not
// part of this union).
export type EnrichmentFeature =
  | 'nameOptimization'
  | 'aiEnrichment'
  | 'emailDomain'
  | 'location';

export interface PayloadConfig {
  /** Field ids ticked in the payload builder. */
  selectedFields: string[];
  /** field id → custom Klaviyo lookup variable (overrides the default). */
  varOverrides: Record<string, string>;
}

export type NameDetail =
  | 'capitalization'
  | 'normalization'
  | 'flipDetection'
  | 'gender'
  | 'age'
  | 'nameParsingFromEmail'
  | 'titleIdentification'
  | 'nameHoliday';

/** Which field uniquely identifies a contact (chosen on the new step 3). */
export type UniqueIdKind = 'klaviyo_id' | 'email' | 'custom';
export interface UniqueId {
  kind: UniqueIdKind;
  /** Field name for the 'email'/'custom' kinds (editable, platform-specific). */
  fieldName: string;
}

/** U-08: a user-added data-input field (canonical key + platform field name). */
export interface ExtraField {
  /** Canonical key emitted in the webhook JSON (e.g. "name"). */
  key: string;
  /** Platform field name / lookup variable the user enters. */
  fieldName: string;
}

export interface SetupState {
  currentStep: number;
  // Step 1
  selectedFeatures: EnrichmentFeature[];
  // Step 2
  selectedNameDetails: NameDetail[];
  // New step 3 — unique identifier choice (drives the webhook primary key and
  // suppresses the data-input email field when email is the identifier).
  uniqueId: UniqueId;
  // Data-input step — the fields the user confirmed (field ids from FIELD_OPTIONS)
  selectedDataInputs: string[];
  // Data-input step — per-field editable platform field names (field id → name).
  fieldNameOverrides: Record<string, string>;
  // Data-input step — F-20 "email address local part only" toggle.
  emailLocalPartOnly: boolean;
  // Data-input step — U-08 user-added extra field mappings.
  extraFields: ExtraField[];
  // Step 4
  selectedReturnMethod: 'csv' | 'api';
  // Klaviyo webhook step
  primaryIdentifier: PrimaryIdentifier;
  payloadConfig: PayloadConfig;
  // Result mode mirror (which accuracy tiers to write back)
  resultMode: OverrideChoice;
  // Step 5
  klaviyoApiKey: string;
  klaviyoConnected: boolean;
  /** T-226: last 4 chars of the key already stored server-side ('' if unknown). */
  klaviyoApiKeyHint: string;

  // Actions
  setCurrentStep: (step: number) => void;
  restartToFirstStep: () => void;
  toggleFeature: (feature: EnrichmentFeature) => void;
  toggleNameDetail: (detail: NameDetail) => void;
  setUniqueId: (uniqueId: UniqueId) => void;
  setSelectedDataInputs: (fields: string[]) => void;
  toggleDataInput: (fieldId: string) => void;
  setFieldNameOverride: (fieldId: string, value: string) => void;
  setEmailLocalPartOnly: (value: boolean) => void;
  addExtraField: () => void;
  updateExtraField: (index: number, patch: Partial<ExtraField>) => void;
  removeExtraField: (index: number) => void;
  setSelectedReturnMethod: (method: 'csv' | 'api') => void;
  setPrimaryIdentifier: (id: PrimaryIdentifier) => void;
  togglePayloadField: (fieldId: string) => void;
  setPayloadFields: (fields: string[]) => void;
  setVarOverride: (fieldId: string, value: string) => void;
  resetPayload: () => void;
  setResultMode: (mode: OverrideChoice) => void;
  setKlaviyoApiKey: (key: string) => void;
  setKlaviyoConnected: (connected: boolean) => void;
  resetSetup: () => void;
  resetSetupWithDefaults: () => void;
  loadFromServer: (config: SetupConfig) => void;
  /** Adopt the server's answer to a save: key stored → drop the local plaintext. */
  applySavedConfig: (config: SetupConfig) => void;
  toConfig: () => SetupConfig;
}

const DEFAULT_PAYLOAD_CONFIG: PayloadConfig = {
  selectedFields: [...DEFAULT_SELECTED_FIELDS],
  varOverrides: {},
};

const DEFAULTS = {
  currentStep: 1,
  selectedFeatures: [] as EnrichmentFeature[],
  selectedNameDetails: [] as NameDetail[],
  uniqueId: { kind: 'email', fieldName: '' } as UniqueId,
  selectedDataInputs: [] as string[],
  fieldNameOverrides: {} as Record<string, string>,
  emailLocalPartOnly: false,
  extraFields: [] as ExtraField[],
  selectedReturnMethod: 'csv' as const,
  primaryIdentifier: 'email' as PrimaryIdentifier,
  payloadConfig: DEFAULT_PAYLOAD_CONFIG,
  resultMode: 'green' as OverrideChoice,
  klaviyoApiKey: '',
  klaviyoConnected: false,
  klaviyoApiKeyHint: '',
};

/**
 * T-173: the worker picks the webhook CSV's unique_id from `primaryIdentifier`,
 * so it must always follow the unique-identifier step (`uniqueId`).
 */
function primaryFromUniqueId(uniqueId: UniqueId): PrimaryIdentifier {
  if (uniqueId.kind === 'klaviyo_id') return 'klaviyo_id';
  if (uniqueId.kind === 'email') return 'email';
  return 'uid';
}

/**
 * What a brand-new account starts the wizard with. Steps 1 and 2 arrive
 * pre-ticked on the cheapest working combination — Name optimization is the only
 * selectable feature box today, and Name cleanup is the base name-detail tier —
 * so a new user can click straight through instead of facing a disabled
 * Continue. Existing accounts re-entering /setup keep their saved selections and
 * never see this.
 */
const NEW_USER_PRESELECTION = {
  selectedFeatures: ['nameOptimization'] as EnrichmentFeature[],
  selectedNameDetails: ['capitalization'] as NameDetail[],
};

export const useSetupStore = create<SetupState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      setCurrentStep: (step) => set({ currentStep: step }),
      // U-04: restart the wizard from step 1 without wiping saved selections.
      restartToFirstStep: () => set({ currentStep: 1 }),
      toggleFeature: (feature) =>
        set((state) => ({
          selectedFeatures: state.selectedFeatures.includes(feature)
            ? state.selectedFeatures.filter((f) => f !== feature)
            : [...state.selectedFeatures, feature],
        })),
      toggleNameDetail: (detail) =>
        set((state) => ({
          selectedNameDetails: state.selectedNameDetails.includes(detail)
            ? state.selectedNameDetails.filter((d) => d !== detail)
            : [...state.selectedNameDetails, detail],
        })),
      setUniqueId: (uniqueId) => set({ uniqueId }),
      setSelectedDataInputs: (fields) => set({ selectedDataInputs: fields }),
      toggleDataInput: (fieldId) =>
        set((state) => ({
          selectedDataInputs: state.selectedDataInputs.includes(fieldId)
            ? state.selectedDataInputs.filter((f) => f !== fieldId)
            : [...state.selectedDataInputs, fieldId],
        })),
      setFieldNameOverride: (fieldId, value) =>
        set((state) => ({
          fieldNameOverrides: { ...state.fieldNameOverrides, [fieldId]: value },
        })),
      setEmailLocalPartOnly: (value) => set({ emailLocalPartOnly: value }),
      addExtraField: () =>
        set((state) => ({ extraFields: [...state.extraFields, { key: '', fieldName: '' }] })),
      updateExtraField: (index, patch) =>
        set((state) => ({
          extraFields: state.extraFields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
        })),
      removeExtraField: (index) =>
        set((state) => ({ extraFields: state.extraFields.filter((_, i) => i !== index) })),
      setSelectedReturnMethod: (method) => set({ selectedReturnMethod: method }),
      setPrimaryIdentifier: (id) => set({ primaryIdentifier: id }),
      togglePayloadField: (fieldId) =>
        set((state) => {
          const has = state.payloadConfig.selectedFields.includes(fieldId);
          return {
            payloadConfig: {
              ...state.payloadConfig,
              selectedFields: has
                ? state.payloadConfig.selectedFields.filter((f) => f !== fieldId)
                : [...state.payloadConfig.selectedFields, fieldId],
            },
          };
        }),
      setPayloadFields: (fields) =>
        set((state) => ({
          payloadConfig: { ...state.payloadConfig, selectedFields: [...fields] },
        })),
      setVarOverride: (fieldId, value) =>
        set((state) => ({
          payloadConfig: {
            ...state.payloadConfig,
            varOverrides: { ...state.payloadConfig.varOverrides, [fieldId]: value },
          },
        })),
      resetPayload: () =>
        set({
          payloadConfig: {
            selectedFields: [...DEFAULT_SELECTED_FIELDS],
            varOverrides: {},
          },
        }),
      setResultMode: (mode) => set({ resultMode: mode }),
      setKlaviyoApiKey: (key) => set({ klaviyoApiKey: key }),
      setKlaviyoConnected: (connected) => set({ klaviyoConnected: connected }),
      resetSetup: () => set({ ...DEFAULTS }),
      // Clean slate PLUS the new-user pre-selection. Kept separate from
      // resetSetup() so account-switch resets in auth.service stay empty.
      resetSetupWithDefaults: () => set({ ...DEFAULTS, ...NEW_USER_PRESELECTION }),
      loadFromServer: (config: SetupConfig) => {
        if (!config || Object.keys(config).length === 0) return;
        set({
          currentStep: config.currentStep ?? DEFAULTS.currentStep,
          selectedFeatures: (config.selectedFeatures ?? DEFAULTS.selectedFeatures) as EnrichmentFeature[],
          selectedNameDetails: (config.selectedNameDetails ?? DEFAULTS.selectedNameDetails) as NameDetail[],
          uniqueId: (config.uniqueId ?? DEFAULTS.uniqueId) as UniqueId,
          selectedDataInputs: config.selectedDataInputs ?? DEFAULTS.selectedDataInputs,
          fieldNameOverrides: config.fieldNameOverrides ?? DEFAULTS.fieldNameOverrides,
          emailLocalPartOnly: config.emailLocalPartOnly ?? DEFAULTS.emailLocalPartOnly,
          extraFields: config.extraFields ?? DEFAULTS.extraFields,
          selectedReturnMethod: config.selectedReturnMethod ?? DEFAULTS.selectedReturnMethod,
          primaryIdentifier: (config.primaryIdentifier ?? DEFAULTS.primaryIdentifier) as PrimaryIdentifier,
          payloadConfig: config.payloadConfig
            ? {
                selectedFields:
                  config.payloadConfig.selectedFields ?? DEFAULT_PAYLOAD_CONFIG.selectedFields,
                varOverrides: config.payloadConfig.varOverrides ?? {},
              }
            : DEFAULT_PAYLOAD_CONFIG,
          resultMode: (config.resultMode ?? DEFAULTS.resultMode) as OverrideChoice,
          // The server never returns the key itself — only whether one is stored.
          klaviyoApiKey: DEFAULTS.klaviyoApiKey,
          klaviyoConnected: config.klaviyoConnected ?? DEFAULTS.klaviyoConnected,
          klaviyoApiKeyHint: config.klaviyoApiKeyHint ?? DEFAULTS.klaviyoApiKeyHint,
        });
      },
      applySavedConfig: (config: SetupConfig) => {
        if (!config) return;
        set({
          klaviyoConnected: !!config.klaviyoConnected,
          klaviyoApiKeyHint: config.klaviyoApiKeyHint ?? '',
          ...(config.klaviyoConnected ? { klaviyoApiKey: '' } : {}),
        });
      },
      toConfig: (): SetupConfig => {
        const s = get();
        return {
          currentStep: s.currentStep,
          selectedFeatures: s.selectedFeatures,
          selectedNameDetails: s.selectedNameDetails,
          uniqueId: s.uniqueId,
          selectedDataInputs: s.selectedDataInputs,
          fieldNameOverrides: s.fieldNameOverrides,
          emailLocalPartOnly: s.emailLocalPartOnly,
          extraFields: s.extraFields,
          selectedReturnMethod: s.selectedReturnMethod,
          primaryIdentifier: primaryFromUniqueId(s.uniqueId),
          payloadConfig: s.payloadConfig,
          resultMode: s.resultMode,
          klaviyoApiKey: s.klaviyoApiKey,
          klaviyoConnected: s.klaviyoConnected,
        };
      },
    }),
    {
      name: 'setup-storage',
      partialize: (state) => ({
        currentStep: state.currentStep,
        selectedFeatures: state.selectedFeatures,
        selectedNameDetails: state.selectedNameDetails,
        uniqueId: state.uniqueId,
        selectedDataInputs: state.selectedDataInputs,
        fieldNameOverrides: state.fieldNameOverrides,
        emailLocalPartOnly: state.emailLocalPartOnly,
        extraFields: state.extraFields,
        selectedReturnMethod: state.selectedReturnMethod,
        primaryIdentifier: state.primaryIdentifier,
        payloadConfig: state.payloadConfig,
        resultMode: state.resultMode,
        klaviyoConnected: state.klaviyoConnected,
        klaviyoApiKeyHint: state.klaviyoApiKeyHint,
      }),
    }
  )
);
